import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import payrollService from "../../services/payrollService";
import SalaryBreakdown from "../../components/Payroll/SalaryBreakdown";
import Notification from "../../components/UI/Notification";
import { Download, AlertCircle } from "lucide-react";

const PayrollDetailsPage = () => {
  const { id } = useParams();
  const [payroll, setPayroll] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState(null);
  const [showCorrectionModal, setShowCorrectionModal] = useState(false);
  const [correctionForm, setCorrectionForm] = useState({ reason: "", details: "" });

  useEffect(() => {
    fetchPayrollDetails();
  }, [id]);

  const fetchPayrollDetails = async () => {
    try {
      const response = await payrollService.getMyPayrollDetails(id);
      if (response.success) {
        setPayroll(response.data);
      }
    } catch (error) {
      setNotification({ type: "error", message: "Failed to fetch payroll details" });
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    try {
      const response = await payrollService.getPayslipDownloadLink(id);
      if (response.success) {
        window.open(response.data.download_url, "_blank");
      }
    } catch (error) {
      setNotification({ type: "error", message: "Failed to download payslip" });
    }
  };

  const handleRequestCorrection = async (e) => {
    e.preventDefault();
    try {
      const response = await payrollService.requestPayrollCorrection(id, correctionForm);
      if (response.success) {
        setNotification({ type: "success", message: "Correction request submitted" });
        setShowCorrectionModal(false);
        setCorrectionForm({ reason: "", details: "" });
      }
    } catch (error) {
      setNotification({ type: "error", message: "Failed to submit correction request" });
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!payroll) {
    return (
      <div className="p-6 font-poppins">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-12 text-center">
          <AlertCircle className="mx-auto text-red-500 mb-4" size={48} />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Payroll Not Found</h3>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 font-poppins">
      {notification && (
        <Notification
          type={notification.type}
          message={notification.message}
          onClose={() => setNotification(null)}
        />
      )}

      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Payroll Details</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            {payroll.payroll_year}-{String(payroll.payroll_month).padStart(2, "0")}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowCorrectionModal(true)}
            className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-md font-medium"
          >
            <AlertCircle size={18} className="inline mr-2" />
            Request Correction
          </button>
          <button
            onClick={handleDownload}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium"
          >
            <Download size={18} className="inline mr-2" />
            Download Payslip
          </button>
        </div>
      </div>

      {/* Payroll Info */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400">Basic Salary</p>
            <p className="text-lg font-semibold text-gray-900 dark:text-white">
              ₹{parseFloat(payroll.basic_salary).toLocaleString("en-IN")}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400">Gross Salary</p>
            <p className="text-lg font-semibold text-gray-900 dark:text-white">
              ₹{parseFloat(payroll.gross_salary).toLocaleString("en-IN")}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400">Total Earnings</p>
            <p className="text-lg font-semibold text-green-600 dark:text-green-400">
              ₹{parseFloat(payroll.total_earnings).toLocaleString("en-IN")}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400">Total Deductions</p>
            <p className="text-lg font-semibold text-red-600 dark:text-red-400">
              ₹{parseFloat(payroll.total_deductions).toLocaleString("en-IN")}
            </p>
          </div>
        </div>
      </div>

      {/* Salary Breakdown */}
      <SalaryBreakdown
        earnings={payroll.earnings_breakdown ? Object.entries(payroll.earnings_breakdown).map(([name, amount]) => ({ name, amount })) : []}
        deductions={payroll.deductions_breakdown ? Object.entries(payroll.deductions_breakdown).map(([name, amount]) => ({ name, amount })) : []}
      />

      {/* Correction Modal */}
      {showCorrectionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1001] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Request Correction
              </h3>
            </div>
            <form onSubmit={handleRequestCorrection} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Reason *
                </label>
                <input
                  type="text"
                  value={correctionForm.reason}
                  onChange={(e) => setCorrectionForm({ ...correctionForm, reason: e.target.value })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  placeholder="Brief reason..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Details *
                </label>
                <textarea
                  value={correctionForm.details}
                  onChange={(e) => setCorrectionForm({ ...correctionForm, details: e.target.value })}
                  required
                  rows="4"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  placeholder="Detailed explanation..."
                />
              </div>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowCorrectionModal(false)}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-md font-medium"
                >
                  Submit Request
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PayrollDetailsPage;

